import {Link, useParams} from "react-router-dom";
import {useContext, useEffect, useRef} from "react";
import {isNullOrWhitespace} from "../../core/helpers/stringHelper";
import {ViewOrderContext} from "../../context/viewOrderContext/ViewOrderContext";
import {ViewOrderContextType} from "../../types/context/ViewOrderContextType";
import {ViewOrderHeader} from "../../components/menu/ViewOrderHeader";
import {ViewMenuItem} from "../../components/menu/ViewMenuItem";
import {IOrderBasketItem} from "../../core/interfaces/IOrderInfo";
import {formatToRandWithDecimalsString as money} from "../../core/helpers/textFormatting";
import {PaymentEnum} from "../../core/enums/PaymentEnum";


export const ViewOrder = () =>
{
    const {
        getOrderInfoFromFirebase,
        pageInfo
    } = useContext(ViewOrderContext) as ViewOrderContextType

    const urlParams = useParams();
    const courseIdParam = urlParams?.courseId ?? '';
    const orderIdParam = urlParams?.orderId ?? '';
    const getOrderInfoRef = useRef(getOrderInfoFromFirebase);
    getOrderInfoRef.current = getOrderInfoFromFirebase;

    useEffect(() =>
    {
        const courseId = courseIdParam;
        const orderId = orderIdParam;

        if (!isNullOrWhitespace(orderId) && !isNullOrWhitespace(courseId))
        {
            getOrderInfoRef.current(courseId, orderId);
        }
    }, [courseIdParam, orderIdParam])

    const order = pageInfo.order;
    const golferDetails = order?.golferDetails?.filter(detail => detail.value) ?? [];
    const subtotal = order?.basketPrice ?? 0;
    const tip = order?.tip ?? 0;

    return (
        <div className='flex flex-col w-full min-h-screen bg-slate-50'>
            <ViewOrderHeader />
            <main className='w-full max-w-2xl mx-auto pb-8'>
                {pageInfo.pageLoading ? (
                    <p role='status' className='p-6 text-slate-500'>Loading receipt…</p>
                ) : pageInfo.error || !order ? (
                    <div className='m-4 rounded-xl bg-white p-5'>
                        <p role='alert' className='text-red-700'>
                            {typeof pageInfo.error === 'string' ? pageInfo.error : 'This order could not be found.'}
                        </p>
                        <button type='button' className='mt-4 font-semibold text-maroon'
                            onClick={() => getOrderInfoFromFirebase(courseIdParam, orderIdParam)}>
                            Retry
                        </button>
                    </div>
                ) : (
                    <>
                        <section aria-label='Order details' className='mx-4 mt-4 p-5 rounded-xl bg-white space-y-2'>
                            <h2 className='text-lg font-bold text-slate-900 break-words'>Order {order.refCode || orderIdParam}</h2>
                            {order.createdDate > 0 && <p className='text-sm text-slate-500'>
                                {new Date(order.createdDate).toLocaleString('en-ZA', {dateStyle: 'medium', timeStyle: 'short'})}
                            </p>}
                            <p className='text-sm text-slate-700'>Order status: {order.state}</p>
                            {order.onCourse && order.hole != null ? <p className='text-sm'>Hole {order.hole}</p>
                                : order.section && <p className='text-sm'>
                                    {order.section === 'frontNine' ? 'Front Nine' : order.section === 'backNine' ? 'Back Nine' : order.section}
                                </p>}
                            <p className='text-sm text-slate-700'>
                                {order.paid ? 'Paid' : 'Not yet paid'}
                                {order.paymentMethod === PaymentEnum.PayAtHouse ? ' - Payment at the Halfway House' : ''}
                            </p>
                        </section>

                        {(golferDetails.length > 0 || (!order.golferDetails && (order.name || order.phone || order.email))) && (
                            <dl aria-label='Golfer details' className='mx-4 my-3 p-5 rounded-xl bg-white space-y-2'>
                                {golferDetails.length > 0 ? golferDetails.map(detail => (
                                    <div key={detail.id} className='flex justify-between gap-4 text-sm'>
                                        <dt className='text-slate-500 break-words min-w-0'>{detail.label}</dt>
                                        <dd className='text-slate-900 break-words text-right min-w-0'>{detail.value}</dd>
                                    </div>
                                )) : [['Name', order.name], ['Phone', order.phone], ['Email', order.email]].filter(([, value]) => value).map(([label, value]) => (
                                    <div key={label} className='flex justify-between gap-4 text-sm'>
                                        <dt className='text-slate-500'>{label}</dt>
                                        <dd className='text-slate-900 break-words text-right min-w-0'>{value}</dd>
                                    </div>
                                ))}
                            </dl>
                        )}

                        <section aria-label='Ordered items' className='py-2'>
                            {pageInfo.basketItems.map((item: IOrderBasketItem, index: number) => (
                                <ViewMenuItem key={index} menuItem={item} />
                            ))}
                        </section>

                        <dl aria-label='Receipt totals' className='mx-4 p-5 rounded-xl bg-white space-y-3'>
                            <div className='flex justify-between gap-4 text-sm'><dt>Subtotal</dt><dd>{money(subtotal)}</dd></div>
                            {tip !== 0 && <div className='flex justify-between gap-4 text-sm'><dt>Tip</dt><dd>{money(tip)}</dd></div>}
                            <div className='flex justify-between gap-4 border-t border-slate-100 pt-3 text-lg font-bold'>
                                <dt>Total</dt><dd>{money(subtotal + tip)}</dd>
                            </div>
                        </dl>
                    </>
                )}
                <Link to={`/${encodeURIComponent(courseIdParam)}`} className='block mx-4 mt-6 button text-center'>
                    Place Another Order
                </Link>
            </main>
        </div>
    );
}
